// hooks/useEnquiryAccess.js
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { axiosInstance, BASE_API_CONFIG } from "../util/Api";
import { DEFAULT_ENQUIRY_CREDIT_CONFIG } from "../util/Constant";
import {
  isCustomhouseagentsCumFreightforwarder,
  isFreightCreditUser,
} from "../util/helper";

export const useEnquiryAccess = () => {
  const [accessStatus, setAccessStatus] = useState({
    isPaidUser: false,
    remainingAccess: 0,
    totalAllowed: 0,
    usedAccess: 0,
    monthlyReset: null,
    availableBalance: 0,
    freeFundsRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freeFunds,
    freeEnquiriesRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freeEnquiries,
    freePortsRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freePorts,
    perEnquiryCharge: DEFAULT_ENQUIRY_CREDIT_CONFIG.perEnquiryCharge,
  });
  const [loading, setLoading] = useState(true);
  const [accessedEnquiries, setAccessedEnquiries] = useState([]);

  const authUser = useSelector(({ auth }) => auth?.userData);
  const userId = useSelector(({ auth }) => auth?.userData?.id);
  const subscription = useSelector(({ auth }) => auth?.userData?.subscription);
  const isFreightUser = isFreightCreditUser(authUser);
  const isCombinedFreightUser =
    isCustomhouseagentsCumFreightforwarder(authUser);

  const normalizeAccessedEnquiries = useCallback((payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }
    return payload?.rows || payload?.data || [];
  }, []);

  const fetchAccessedEnquiries = useCallback(async () => {
    if (!isFreightUser) {
      try {
        const response = await axiosInstance.get("/enquiries/accessed/id");
        setAccessedEnquiries(response?.data?.data || []);
      } catch (legacyError) {
        console.error("Error fetching accessed enquiries:", legacyError);
      }
      return;
    }

    try {
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/wallet/access-history`
      );
      setAccessedEnquiries(
        normalizeAccessedEnquiries(response?.data?.data ?? response?.data)
      );
    } catch (error) {
      try {
        const response = await axiosInstance.get("/enquiries/accessed/id");
        setAccessedEnquiries(response?.data?.data || []);
      } catch (legacyError) {
        console.error("Error fetching accessed enquiries:", legacyError);
        setAccessedEnquiries([]);
      }
    }
  }, [isFreightUser, normalizeAccessedEnquiries]);

  useEffect(() => {
    const fetchAccessStatus = async () => {
      try {
        setLoading(true);

        if (
          subscription &&
          [2, 3].includes(subscription.planId) &&
          !isCombinedFreightUser
        ) {
          setAccessStatus({
            isPaidUser: true,
            remainingAccess: "unlimited",
            totalAllowed: "unlimited",
            usedAccess: "unlimited",
            monthlyReset: null,
            availableBalance: "unlimited",
            freeFundsRemaining: "unlimited",
            freeEnquiriesRemaining: "unlimited",
            freePortsRemaining: "unlimited",
            perEnquiryCharge: DEFAULT_ENQUIRY_CREDIT_CONFIG.perEnquiryCharge,
          });
          await fetchAccessedEnquiries();
          return;
        }

        if (isFreightUser) {
          try {
            const response = await axiosInstance.get(
              `${BASE_API_CONFIG.baseURL}/wallet/access-summary`
            );
            const payload = response?.data?.data ?? response?.data ?? {};
            setAccessStatus({
              isPaidUser: false,
              remainingAccess:
                payload.remainingAccess ??
                payload.freeEnquiriesRemaining ??
                0,
              totalAllowed:
                payload.totalAllowed ??
                payload.freeEnquiries ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.freeEnquiries,
              usedAccess:
                payload.usedAccess ?? payload.freeEnquiriesUsed ?? 0,
              monthlyReset: payload.monthlyReset ?? null,
              availableBalance:
                payload.availableBalance ?? payload.walletBalance ?? 0,
              freeFundsRemaining:
                payload.freeFundsRemaining ??
                payload.freeCreditsRemaining ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.freeFunds,
              freeEnquiriesRemaining:
                payload.freeEnquiriesRemaining ??
                payload.remainingAccess ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.freeEnquiries,
              freePortsRemaining:
                payload.freePortsRemaining ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.freePorts,
              perEnquiryCharge:
                payload.perEnquiryCharge ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.perEnquiryCharge,
            });
          } catch (walletError) {
            const response = await axiosInstance.get("/enquiries/access/status");
            setAccessStatus({
              ...response.data,
              availableBalance: response?.data?.availableBalance ?? 0,
              freeFundsRemaining:
                response?.data?.freeFundsRemaining ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.freeFunds,
              freeEnquiriesRemaining:
                response?.data?.remainingAccess ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.freeEnquiries,
              freePortsRemaining:
                response?.data?.freePortsRemaining ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.freePorts,
              perEnquiryCharge:
                response?.data?.perEnquiryCharge ??
                DEFAULT_ENQUIRY_CREDIT_CONFIG.perEnquiryCharge,
            });
          }
        } else {
          const response = await axiosInstance.get("/enquiries/access/status");
          setAccessStatus({
            ...response.data,
            availableBalance: 0,
            freeFundsRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freeFunds,
            freeEnquiriesRemaining:
              response?.data?.remainingAccess ??
              DEFAULT_ENQUIRY_CREDIT_CONFIG.freeEnquiries,
            freePortsRemaining: DEFAULT_ENQUIRY_CREDIT_CONFIG.freePorts,
            perEnquiryCharge:
              response?.data?.perEnquiryCharge ??
              DEFAULT_ENQUIRY_CREDIT_CONFIG.perEnquiryCharge,
          });
        }
        await fetchAccessedEnquiries();
      } catch (error) {
        console.error("Error fetching access status:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchAccessStatus();
    }
  }, [
    userId,
    subscription,
    fetchAccessedEnquiries,
    isFreightUser,
    isCombinedFreightUser,
  ]);

  const canAccessEnquiry = (enquiryId) => {
    if (accessStatus.isPaidUser) return true;

    const isAccessed = accessedEnquiries.some(
      (enquiry) =>
        enquiry?.id === enquiryId || enquiry?.enquiryId === enquiryId
    );

    if (isFreightUser) {
      return isAccessed;
    }

    return isAccessed || accessStatus.remainingAccess > 0;
  };

  return {
    ...accessStatus,
    accessedEnquiries,
    loading,
    canAccessEnquiry,
    refresh: fetchAccessedEnquiries,
  };
};
